import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/config';
import { Table, Button, Modal, Typography, Card, Row, Col, Input, Tag, Space, Avatar, Tooltip } from 'antd';
import { 
  DollarOutlined,
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CloseCircleOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  UserOutlined,
  BankOutlined,
  UndoOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import InvestorRegister from '../../components/auth/InvestorRegister';
import RejectReasonModal from '../../components/admin/RejectReasonModal';

const { Title, Text } = Typography;

const InvestorManagement = () => {
  const [investors, setInvestors] = useState([]);
  const [filteredInvestors, setFilteredInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch investors from API
  const loadInvestors = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/investors');
      if (response.data && response.data.success) {
        setInvestors(response.data.investors);
        setFilteredInvestors(response.data.investors);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch investors');
      }
    } catch (error) {
      console.error('Error loading investors:', error);
      toast.error('Error loading investors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvestors();
  }, []);

  // Search Logic
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    const filtered = investors.filter((investor) => 
      investor.investorName?.toLowerCase().includes(value) ||
      investor.contactPerson?.toLowerCase().includes(value) ||
      investor.email?.toLowerCase().includes(value) ||
      investor.phone?.includes(value) ||
      investor.companyName?.toLowerCase().includes(value) ||
      investor.investmentStage?.toLowerCase().includes(value) ||
      investor.investmentFocus?.some(focus => focus.toLowerCase().includes(value))
    );
    setFilteredInvestors(filtered);
  };

  const handleReset = () => {
    setSearchText('');
    setFilteredInvestors(investors);
  };

  // Toggle status (approved/pending/rejected)
  const handleToggleStatus = async (investorId, currentStatus) => {
    Modal.confirm({
      title: 'Change Investor Status',
      content: (
        <div>
          <p>Current status: <Tag color={getStatusColor(currentStatus)}>{currentStatus}</Tag></p>
          <p>Toggle to {currentStatus === 'approved' ? 'pending' : 'approved'}?</p>
        </div>
      ),
      okText: 'Update',
      cancelText: 'Cancel',
      onOk: async () => {
        const newStatus = currentStatus === 'approved' ? 'pending' : 'approved';
        try {
          const response = await api.put(`/admin/investors/${investorId}/status`, {
            status: newStatus
          });
          
          if (response.data && response.data.success) {
            toast.success(`Investor status updated to ${newStatus}`);
            loadInvestors();
          }
        } catch (error) {
          console.error('Error updating status:', error);
          toast.error('Failed to update status');
        }
      }
    });
  };

  // Approve / Reject registration status
  const handleApprove = (investorId) => {
    Modal.confirm({
      title: 'Approve Investor Registration',
      content: 'Are you sure you want to approve this investor?',
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        try {
          const response = await api.put(`/admin/investors/${investorId}/approve`);
          if (response.data && response.data.success) {
            toast.success('Investor approved successfully');
            loadInvestors();
          }
        } catch (error) {
          toast.error('Failed to approve investor');
        }
      }
    });
  };

  const handleRevert = (investorId) => {
    Modal.confirm({
      title: 'Revert to Pending',
      content: 'This will move the record back to Pending review and it will be hidden from the public site if currently approved. Continue?',
      okText: 'Revert',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await api.put(`/admin/investors/${investorId}/revert`);
          if (response.data && response.data.success) {
            toast.success('Investor reverted to pending');
            loadInvestors();
          }
        } catch (error) {
          toast.error('Failed to revert investor');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleReject = (investor) => {
    setRejectTarget(investor);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.put(`/admin/investors/${rejectTarget._id}/reject`, {
        reason: rejectReason
      });
      if (response.data && response.data.success) {
        toast.success('Investor rejected');
        setRejectModalOpen(false);
        loadInvestors();
      }
    } catch (error) {
      toast.error('Failed to reject investor');
    } finally {
      setActionLoading(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    setEditingInvestor(null);
    setIsModalOpen(true);
  };

  const handleEdit = (investor) => {
    setEditingInvestor(investor);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setTimeout(() => setEditingInvestor(null), 300);
  };

  const handleEditSuccess = () => {
    handleModalClose();
    loadInvestors();
  };

  // Delete Investor
  const handleDelete = (investorId) => {
    Modal.confirm({
      title: 'Delete Investor',
      content: 'Are you sure you want to delete this investor? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/investors/${investorId}`);
          
          if (response.data && response.data.success) {
            toast.success('Investor deleted successfully');
            loadInvestors();
          } else {
            throw new Error(response.data?.message || 'Failed to delete investor');
          }
        } catch (error) {
          toast.error('Failed to delete investor.');
        }
      }
    });
  };

  // Helper function for status colors
  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  // Helper function for investment stage colors
  const getStageColor = (stage) => {
    const colors = {
      'pre-seed': 'purple',
      'seed': 'blue',
      'early': 'cyan',
      'growth': 'green',
      'late': 'orange',
      'series a': 'geekblue',
      'series b': 'magenta',
      'series c+': 'red'
    };
    return colors[stage?.toLowerCase()] || 'default';
  };

  // Statistics Calculation
  const stats = {
    total: investors.length,
    approved: investors.filter(i => i.status === 'approved').length,
    active: investors.filter(i => i.isActive).length,
    inactive: investors.filter(i => !i.isActive).length
  };

  // Styles based on the screenshot
  const cardStyles = {
    total: { bg: '#e6f7ff', color: '#1890ff', icon: <DollarOutlined /> },
    approved: { bg: '#f6ffed', color: '#52c41a', icon: <CheckCircleOutlined /> },
    active: { bg: '#fff7e6', color: '#fa8c16', icon: <CheckCircleOutlined /> },
    inactive: { bg: '#fff1f0', color: '#ff4d4f', icon: <CloseCircleOutlined /> }
  };

  // Table columns
  const columns = [
    {
      title: <span style={{ fontWeight: 500 }}>#</span>,
      key: 'index',
      width: 60,
      render: (text, record, index) => <Text style={{ color: '#666', fontSize:"16px" }}>{index + 1}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Investor Name</span>,
      dataIndex: 'investorName',
      key: 'investorName',
      render: (text, record) => {
        // Determine which logo to use
        let logoUrl = '';
        const logoSource = record.investorType === 'company' 
          ? (record.companyLogo || record.logo)
          : (record.profilePicture || record.logo);
        
        if (logoSource) {
          if (logoSource.startsWith('http')) {
            logoUrl = logoSource;
          } else if (logoSource.startsWith('/')) {
            logoUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${logoSource}`;
          } else {
            logoUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${logoSource}`;
          }
        }
        
        return (
          <Space>
            {logoUrl ? (
              <Avatar src={logoUrl} size={40} />
            ) : (
              <Avatar style={{ backgroundColor: '#3C007A' }} size={40} icon={record.investorType === 'company' ? <BankOutlined /> : <UserOutlined />} />
            )}
            <div>
              <Text strong style={{ color: '#333', fontSize:"15px", display: 'block', wordWrap: 'break-word', maxWidth: '200px' }}>{text}</Text>
              <Space size={4}>
                <Tag color={record.investorType === 'company' ? 'blue' : 'green'}>
                  {record.investorType}
                </Tag>
                {record.companyName && (
                  <Text type="secondary" style={{ fontSize:"12px" }}>{record.companyName}</Text>
                )}
              </Space>
            </div>
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontWeight: 500 }}>Contact Info</span>,
      key: 'contact',
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize:"14px" }}>
            <MailOutlined style={{ marginRight: 5, color: '#1890ff' }} />
            {record.email}
          </Text>
          <Text style={{ fontSize:"14px" }}>
            <PhoneOutlined style={{ marginRight: 5, color: '#52c41a' }} />
            {record.phone}
          </Text>
          <Text style={{ fontSize:"13px", color: '#888' }}>
            {record.contactPerson}
          </Text>
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Investment Stage</span>,
      dataIndex: 'investmentStage',
      key: 'investmentStage',
      render: (stage) => (
        <Tag color={getStageColor(stage)}>
          {stage}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Status</span>,
      key: 'status',
      render: (record) => (
        <Space direction="vertical" size={4}>
          <Tag color={getStatusColor(record.status)} icon={record.status === 'approved' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}>
            {record.status}
          </Tag>
          {record.isActive ? (
            <Tag color="green">Active</Tag>
          ) : (
            <Tag color="red">Inactive</Tag>
          )}
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Created</span>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Text style={{ fontSize:"13px", color: '#666' }}>
          {moment(date).format('MMM DD, YYYY')}
        </Text>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Actions</span>,
      key: 'actions',
      width: 120,
      render: (record) => (
        <Space size="small">
          {record.status === 'pending' && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="link"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApprove(record._id)}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="link"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleReject(record)}
                />
              </Tooltip>
            </>
          )}
          {(record.status === 'approved' || record.status === 'rejected') && (
            <Tooltip title="Revert to Pending">
              <Button
                type="link"
                icon={<UndoOutlined />}
                onClick={() => handleRevert(record._id)}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px', fontFamily: "'Rubik', sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, color: '#333', fontFamily: "'Rubik', sans-serif" }}>
          Investor Management
        </Title>
        <Text type="secondary" style={{ fontFamily: "'Rubik', sans-serif" }}>
          Manage all investors in the ecosystem
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.total.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.total.color }}>{cardStyles.total.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.total.color, display: 'block' }}>
                  {stats.total}
                </Text>
                <Text type="secondary">Total Investors</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.approved.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.approved.color }}>{cardStyles.approved.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.approved.color, display: 'block' }}>
                  {stats.approved}
                </Text>
                <Text type="secondary">Approved</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.active.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.active.color }}>{cardStyles.active.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.active.color, display: 'block' }}>
                  {stats.active}
                </Text>
                <Text type="secondary">Active</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.inactive.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.inactive.color }}>{cardStyles.inactive.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.inactive.color, display: 'block' }}>
                  {stats.inactive}
                </Text>
                <Text type="secondary">Inactive</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Search and Filter */}
      <div style={{ 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '8px 8px 0 0', 
        marginBottom: '0px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '200px' }}>
          <Input 
            placeholder="Search investors by name, email, phone, stage, or focus area"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            style={{ maxWidth: '400px', fontFamily: "'Rubik', sans-serif" }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Reset
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <Text type="secondary" className="hidden md:block">
            Showing {filteredInvestors.length} of {investors.length} records
          </Text>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={openAddModal}
            style={{ backgroundColor: '#1890ff', borderRadius: '4px' }}
          >
            Add Investor
          </Button>
        </div>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredInvestors}
        loading={loading}
        rowKey={(record) => record._id}
        pagination={{ 
          pageSize: 10, 
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items` 
        }}
        scroll={{ x: 'max-content' }}
        rowClassName="align-middle"
        style={{ background: '#fff', borderRadius: '0 0 8px 8px' }}
      />

      {/* Edit Modal */}
      <Modal
        title={editingInvestor ? 'Edit Investor' : 'Add New Investor'}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={1000}
        centered
        destroyOnClose
      >
        <InvestorRegister
          editingInvestor={editingInvestor}
          isAdminEdit={true}
          onSuccess={handleEditSuccess}
        />
      </Modal>

      <RejectReasonModal
        open={rejectModalOpen}
        title="Reject Investor Registration"
        entityLabel={rejectTarget?.investorName}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        onOk={submitRejection}
        onCancel={() => setRejectModalOpen(false)}
        confirmLoading={actionLoading}
      />
    </div>
  );
};

export default InvestorManagement;
